import "./SecureTextInput.scss";

import { FunctionComponent, useState, useEffect } from "react";
import { TextField } from "@mui/material";
import { SecureTextInputProps } from "./ISecureTextInputProps";

const SecureTextInput: FunctionComponent<SecureTextInputProps & { inputRef?: React.Ref<HTMLInputElement> }> = (props) => {
  const {
    id,
    name,
    placeholder,
    variant,
    label,
    color,
    required,
    className,
    sx,
    value,
    defaultValue,
    error,
    disabled,
    readOnly,
    fullWidth,
    type,
    inputClassName,
    helperText,
    InputProps,
    shrink,
    inputLableStyle,
    size = "medium",
    labelSize,
    onChange,
    onBlur,
    onFocus,
    onClick,
    tabIndex,
    inputRef,
    multiline,
    rows,
    emojiErrorMessage = "Emojis and stickers are not allowed",
    showEmojiWarning = true,
    blockEmojis = true,
  } = props;

  const [hasEmoji, setHasEmoji] = useState(false);
  const [internalValue, setInternalValue] = useState<any>(value || defaultValue || "");

  // Regex pattern for emojis
  const emojiPattern = "[\\p{Extended_Pictographic}\\p{Emoji_Presentation}]";

  // Function to remove emojis from text
  const removeEmojis = (text: string): string => {
    return text.replace(new RegExp(emojiPattern, 'gu'), '');
  };

  // Function to check if text contains emojis
  const containsEmojis = (text: string): boolean => {
    return new RegExp(emojiPattern, 'u').test(text);
  };

  // Update internal value when prop value changes
  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  // Handle input change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = event.target.value;

    // Check if original input had emojis
    const hadEmojis = containsEmojis(newValue);
    setHasEmoji(hadEmojis);

    // Only remove emojis if blockEmojis is true
    const finalValue = blockEmojis ? removeEmojis(newValue) : newValue;

    // Force DOM update if value changed, preventing React desync
    if (newValue !== finalValue) {
      event.target.value = finalValue;
    }

    // Update internal state
    setInternalValue(finalValue);

    // Create a new event with the appropriate value
    const cleanedEvent = {
      ...event,
      target: {
        ...event.target,
        value: finalValue
      }
    };

    // Call the original onChange handler if provided
    if (onChange) {
      onChange(cleanedEvent);
    }
  };

  // Handle blur to clear emoji warning
  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setHasEmoji(false);
    if (onBlur) {
      onBlur(event);
    }
  };

  const showError = error || (showEmojiWarning && hasEmoji && blockEmojis);

  return (
    <TextField
      id={id}
      tabIndex={tabIndex}
      name={name}
      variant={variant}
      label={label}
      color={color}
      placeholder={placeholder}
      autoComplete="on"
      required={required}
      fullWidth={fullWidth}
      className={`GlobalInputStyle ${className} ${showError ? 'error' : ''}`}
      sx={sx}
      value={internalValue}
      defaultValue={defaultValue}
      error={showError}
      helperText={showError && hasEmoji && blockEmojis ? emojiErrorMessage : helperText}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={onFocus}
      onClick={onClick}
      disabled={disabled}
      type={type ?? "text"}
      size={size}
      multiline={multiline}
      rows={rows}
      InputProps={{
        ...InputProps,
        readOnly: readOnly,
        className: `GlobalInputPropsStyle ${inputClassName}`,
      }}
      InputLabelProps={{
        shrink: shrink,
        size: labelSize,
        className: `GlobalInputLableStyle ${inputLableStyle}`,
      }}
      inputRef={inputRef}
    />
  );
};

export default SecureTextInput;
